import { AbstractControl, AbstractControlOptions, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Moment } from 'moment';
import moment from 'moment-timezone';

import { EditorBookingItemSetting } from './editor-booking-item-setting.model';
import { EditorDataService } from '../editor-data/editor-data.service';
import { timezoneValidator } from '../editor-data/timezone-validator.directive';


const settingValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.pristine) {
    return null;
  }

  const errors = { };

  const openDateSet = control.get('openDate').value !== null;
  const openIsEmpty = !openDateSet
    && control.get('openHour').value === null
    && control.get('openMinute').value === null;

  const closeDateSet = control.get('closeDate').value !== null;
  const closeIsEmpty = !closeDateSet
    && control.get('closeHour').value === null
    && control.get('closeMinute').value === null;

  if (!openIsEmpty && !openDateSet) {
    errors['invalidOpen'] = true;
  }

  if (!closeIsEmpty && !closeDateSet) {
    errors['invalidClose'] = true;
  }

  if (openIsEmpty && closeIsEmpty) {
    errors['invalidWindow'] = true;
  }

  return !!errors ? errors : null;
};


export class EditorBookingItemSettingBookingWindow extends EditorBookingItemSetting {
  settingClassName = 'BookingWindow';
  serializedName = 'bookingWindow';
  showSummary = true;

  open: Moment;
  close: Moment;
  timezone: string;

  constructor(obj?: any) {
    super({
      inheritableType: obj?.inheritableType ?? null
    });
    this.open = obj?.open ?? null;
    this.close = obj?.close ?? null;
    this.timezone = obj?.timezone ?? 'Europe/London';
  }

  createCopy(): EditorBookingItemSettingBookingWindow {
    return new EditorBookingItemSettingBookingWindow({
      open: this.open,
      close: this.close,
      timezone: this.timezone,
      inheritableType: this.inheritableType
    });
  }

  getTitle(): string {
    return $localize `Booking window`;
  }

  getHelpText(): string {
    return $localize `During which dates and times should this be available to book?`;
  }

  hasSummary(): boolean {
    return this.open !== null || this.close !== null;
  }

  getFormConfig(editorData: EditorDataService): {
    controlsConfig: { [key: string]: any; },
    options?: AbstractControlOptions
  } {
    return {
      controlsConfig: {
        openDate: [this.open === null ? null : this.open.clone().startOf('date')],
        openHour: [this.open === null ? null : this.open.hour()],
        openMinute: [this.open === null ? null : this.open.minute()],
        closeDate: [this.close === null ? null : this.close.clone().startOf('date')],
        closeHour: [this.close === null ? null : this.close.hour()],
        closeMinute: [this.close === null ? null : this.close.minute()],
        timezone: [this.timezone, timezoneValidator(editorData.tzNames)]
      },
      options: {
        validators: settingValidator
      }
    };
  }

  getFormValues(): any {
    return {
      openDate: this.open === null ? null : this.open.clone().startOf('date'),
      openHour: this.open === null ? null : this.open.hour(),
      openMinute: this.open === null ? null : this.open.minute(),
      closeDate: this.close === null ? null : this.close.clone().startOf('date'),
      closeHour: this.close === null ? null : this.close.hour(),
      closeMinute: this.close === null ? null : this.close.minute(),
      timezone: this.timezone
    };
  }

  getEmptyFormValues(): any {
    return {
      openDate: null,
      openHour: null,
      openMinute: null,
      closeDate: null,
      closeHour: null,
      closeMinute: null,
      timezone: 'Europe/London'
    };
  }

  applyFormValues(formValues: any) {
    let open: Moment = null;
    if (formValues.openDate !== null) {
      open = moment(formValues.openDate.format('YYYY-MM-DD'));
      if (formValues.openHour !== null) {
        open.hour(formValues.openHour);
      }
      if (formValues.openMinute !== null) {
        open.minute(formValues.openMinute);
      }
    }

    let close: Moment = null;
    if (formValues.closeDate !== null) {
      close = moment(formValues.closeDate.format('YYYY-MM-DD'));
      if (formValues.closeHour !== null) {
        close.hour(formValues.closeHour);
      }
      if (formValues.closeMinute !== null) {
        close.minute(formValues.closeMinute);
      }
    }

    this.open = open;
    this.close = close;
    this.timezone = formValues.timezone;
  }

  getSerializedValues(): any {
    // TODO: decide for a default timezone when not set
    // const timezone = !this.timezone ? moment.tz.guess() : this.timezone;
    const timezone = !this.timezone ? 'Europe/London' : this.timezone;

    const open: Moment = this.open !== null
      ? moment.tz(this.open.format('YYYY-MM-DD HH:mm'), timezone).tz('UTC')
      : this.open;

    const close: Moment = this.close !== null
      ? moment.tz(this.close.format('YYYY-MM-DD HH:mm'), timezone).tz('UTC')
      : this.close;

    return {
      startDate: open === null ? null : open.toISOString(),
      endDate: close === null ? null : close.toISOString(),
      timezone
    };
  }
}
